import {
  httpLink,
  httpSubscriptionLink,
  splitLink,
  TRPCClientError,
  type TRPCLink,
} from '@trpc/client'
import { createTRPCNext } from '@trpc/next'
import { observable } from '@trpc/server/observable'

import { runInferenceInWorker } from '@/services/bayesianApi/inferenceWorkerClient'
import {
  runsInInferenceWorker,
  type InferenceWorkerPath,
} from '@/services/bayesianApi/inferenceWorkerProtocol'
import queryClient from '@/services/queryClient'

import type { AppRouter } from '../server/routers/_app'

/**
 * `LBP_IN_BROWSER` inference, resolved in a Web Worker instead of over HTTP
 * (KBE-840).
 *
 * A terminating LINK rather than a branch in each hook, because the engine choice is a
 * transport decision and nothing above it should have to know one was made. The three
 * hooks, `useDiagnosisError`, and `AssumptionSetChangeContainer`'s imperative
 * `trpcUtils.bayesian.multipleInfer.fetch` all keep their existing query keys, their
 * shared cache entries, their `select`, their `retry` and their `meta.onError` -- had
 * the branch gone into the hooks, the imperative call site would have needed the same
 * key built a second way, and one of the two would eventually have drifted and stopped
 * sharing the cache.
 *
 * The request already carries everything the engine needs, so the link decides from
 * `op.input` alone (gotcha 38).
 *
 * A worker failure arrives as a `TRPCClientError`, which means `shouldRetryInference`
 * treats it like an unreachable Modal: retried three times, then surfaced. The cost is
 * NOT milliseconds -- the shared `retryDelay` is 1s/2s/4s, so a deterministic worker
 * failure takes ~7s and four engine runs to reach the user. Still the right end of the
 * trade, because the alternative is a transient spawn failure permanently blanking the
 * Diagnosis page with no recovery short of a reload (gotcha 29); 7s of spinner beats
 * that. If local failures ever become common rather than transient, revisit it.
 */
const inferenceWorkerLink: TRPCLink<AppRouter> =
  () =>
  ({ op }) =>
    observable((observer) => {
      const startedAt = performance.now()
      runInferenceInWorker(op.path as InferenceWorkerPath, op.input).then(
        (data) => {
          console.info(
            `[bayesian] ${op.path} engine=LBP_IN_BROWSER ` +
              `worker=${Math.round(performance.now() - startedAt)}ms`,
          )
          observer.next({ result: { data } })
          observer.complete()
        },
        (error: unknown) => {
          observer.error(
            TRPCClientError.from(
              error instanceof Error ? error : new Error(String(error)),
            ),
          )
        },
      )
    })

function getBaseUrl() {
  if (typeof window !== 'undefined')
    // browser should use relative path
    return ''

  if (process.env.VERCEL_URL)
    // reference for vercel.com
    return `https://${process.env.VERCEL_URL}`

  // assume localhost
  return `http://localhost:${process.env.PORT ?? 3000}`
}

export const trpc = createTRPCNext<AppRouter>({
  config() {
    return {
      // Use the shared queryClient so we can invalidate tRPC queries from outside React
      queryClient,
      links: [
        splitLink({
          condition: (op) => runsInInferenceWorker(op.path, op.input),
          true: inferenceWorkerLink,
          // Subscriptions (assistant.streamTurn) need a Server-Sent
          // Events transport that buffers nothing. The httpSubscriptionLink
          // requires a Fetch-API endpoint, so subscriptions are routed to
          // the App Router handler at /api/trpc-sse while queries and
          // mutations stay on the existing Pages API handler at /api/trpc.
          false: splitLink({
            condition: (op) => op.type === 'subscription',
            true: httpSubscriptionLink({
              url: `${getBaseUrl()}/api/trpc-sse`,
            }),
            false: httpLink({
              /**
               * If you want to use SSR, you need to use the server's full URL
               * @link https://trpc.io/docs/v11/ssr
               **/
              url: `${getBaseUrl()}/api/trpc`,
              // using POST because of large bayesian requests
              methodOverride: 'POST',
            }),
          }),
        }),
      ],
    }
  },
  /**
   * @link https://trpc.io/docs/v11/ssr
   **/
  ssr: false,
})
